import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchThemeData } from "../../admin/themeSlice";
import Swal from "sweetalert2";
import "./jigsaw-game.css";
import { setBackButtonUrl } from "../uiSlice";
import { sendReport } from "../../functions/sendReport";
import { fetchReport } from "../../functions/fetchReport";
import JigsawPuzzle from "./JigsawPuzzle";
import {
  pickPuzzleTypeFromThemeData,
  PUZZLE_SHAPE_LABELS,
} from "../../puzzleShapeFromTheme";
import {
  buildThemeAssetUrl,
  themeBackgroundImageUrl,
} from "../../utils/themeAssetUrl";

function parseThemeRules(val) {
  if (Array.isArray(val)) return val.filter(Boolean);
  if (typeof val === "string") {
    try {
      const p = JSON.parse(val);
      return Array.isArray(p) ? p.filter(Boolean) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function parseGridDim(val, fallback) {
  const n = parseInt(String(val), 10);
  if (Number.isNaN(n)) return fallback;
  return Math.max(1, Math.min(20, n));
}

/** Whether the theme enforces a countdown (stored as string in DB). */
function themeUsesCountdownTimer(themeData) {
  if (!themeData) return true;
  const v = themeData.use_timeout;
  if (v === undefined || v === null || v === "") return true;
  if (v === true) return true;
  if (v === false) return false;
  const s = String(v).toLowerCase().trim();
  if (s === "false" || s === "0" || s === "no" || s === "off") return false;
  if (s === "true" || s === "1" || s === "yes" || s === "on") return true;
  return true;
}

/** Theme timer string "MM:SS" (minutes:seconds) → total seconds, clamped */
function parseThemeTimer(timerVal) {
  if (!timerVal || typeof timerVal !== "string") return { totalSeconds: 300 };
  const parts = timerVal.split(":");
  if (parts.length >= 2) {
    const minutes = Math.max(0, parseInt(parts[0], 10) || 0);
    const seconds = Math.min(59, Math.max(0, parseInt(parts[1], 10) || 0));
    const total = minutes * 60 + seconds;
    return { totalSeconds: Math.max(1, Math.min(86400, total)) };
  }
  return { totalSeconds: 300 };
}

function formatClock(totalSeconds) {
  const s = Math.max(0, totalSeconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (x) => (x < 10 ? `0${x}` : String(x));
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

function formatDisplayMmSs(totalSeconds) {
  const s = Math.max(0, totalSeconds);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m < 10 ? `0${m}` : m}:${sec < 10 ? `0${sec}` : sec}`;
}

/** Spoken-style label for thank-you copy, e.g. "5 minutes and 30 seconds" */
function formatGivenTimeLabel(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m === 0) return `${sec} second${sec === 1 ? "" : "s"}`;
  if (sec === 0) return `${m} minute${m === 1 ? "" : "s"}`;
  return `${m} minute${m === 1 ? "" : "s"} and ${sec} second${sec === 1 ? "" : "s"}`;
}

function pickJigsawPieceType(themeData) {
  const allowed = ["classic", "square", "irregular"];
  const raw =
    typeof themeData?.selected_jigsaw_type === "string"
      ? themeData.selected_jigsaw_type.toLowerCase().trim()
      : "";
  if (allowed.includes(raw)) return raw;
  return "classic";
}

function sessionIdStartsWithDemo(user) {
  const sid = user?.sessionId;
  return typeof sid === "string" && sid.toLowerCase().startsWith("demo");
}

/** Short demo timer, skip report fetch, thank-you without full submit. */
function isDemoGameBypass(user) {
  if (!user) return false;
  if (user.role === "demobypass") return true;
  return sessionIdStartsWithDemo(user);
}

function redirectToPlansPage(navigate) {
  const planUrl =
    typeof process.env.REACT_APP_PLAN_URL === "string"
      ? process.env.REACT_APP_PLAN_URL.trim()
      : "";
  const base =
    typeof process.env.REACT_APP_BASE_URL === "string"
      ? process.env.REACT_APP_BASE_URL.trim().replace(/\/$/, "")
      : "";
  const resolved = planUrl || (base ? `${base}/plans` : "");
  // react-router `navigate("https://…")` is treated as a relative path — use full navigation for absolute URLs
  if (resolved && /^https?:\/\//i.test(resolved)) {
    window.location.assign(resolved);
    return;
  }
  navigate(resolved || "/plans");
}

/** Legacy countdown key (v1) — migrated on read when possible. */
const JIGSAW_TIMER_STORAGE_V1 = "jigsawPuzzleDeadline:v1";
const JIGSAW_COUNTDOWN_V2 = "jigsawPuzzleDeadline:v2:";
const JIGSAW_ELAPSED_V1 = "jigsawPuzzleElapsed:v1:";

function legacyCountdownStorageKey(user) {
  if (!user?.sessionId || !user?.userId) return null;
  return `${JIGSAW_TIMER_STORAGE_V1}:${user.sessionId}:${user.userId}:${user.organizationId || ""}`;
}

/** Stable id so countdown / elapsed survive refresh (theme + grid + user or anon). */
function puzzleTimerSessionId(themeData, user, rows, cols) {
  const tn =
    (typeof themeData?.themename === "string" && themeData.themename) ||
    (typeof themeData?.themeName === "string" && themeData.themeName) ||
    "default";
  const grid = `${rows}x${cols}`;
  const sid = user?.sessionId;
  const uid = user?.userId;
  if (sid && uid) {
    return `${tn}|${grid}|${sid}|${uid}|${user?.organizationId ?? ""}`;
  }
  return `${tn}|${grid}|anon`;
}

function countdownStorageKey(themeData, user, rows, cols) {
  return `${JIGSAW_COUNTDOWN_V2}${puzzleTimerSessionId(themeData, user, rows, cols)}`;
}

function elapsedStorageKey(themeData, user, rows, cols) {
  return `${JIGSAW_ELAPSED_V1}${puzzleTimerSessionId(themeData, user, rows, cols)}`;
}

function readCountdownPersisted(key) {
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (
      o &&
      typeof o.deadline === "number" &&
      typeof o.allocated === "number"
    ) {
      return o;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function writeCountdownPersisted(key, deadlineMs, allocatedSec) {
  if (!key) return;
  sessionStorage.setItem(
    key,
    JSON.stringify({ deadline: deadlineMs, allocated: allocatedSec })
  );
}

function readElapsedPersisted(key) {
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (o && typeof o.seconds === "number" && Number.isFinite(o.seconds)) {
      return o;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function writeElapsedPersisted(key, seconds) {
  if (!key) return;
  sessionStorage.setItem(
    key,
    JSON.stringify({ seconds: Math.min(86400, Math.max(0, seconds)) })
  );
}

/** Clears countdown + elapsed session keys for this puzzle (and legacy v1 countdown if present). */
function clearPuzzleTimerSession(themeData, user, rows, cols) {
  if (!themeData) return;
  sessionStorage.removeItem(countdownStorageKey(themeData, user, rows, cols));
  sessionStorage.removeItem(elapsedStorageKey(themeData, user, rows, cols));
  const legacy = legacyCountdownStorageKey(user);
  if (legacy) sessionStorage.removeItem(legacy);
}

function Game() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: themeData, status: themeStatus } = useSelector((state) => state.theme);
  const { status, user } = useSelector((state) => state.auth);

  /** Countdown: seconds left. No countdown: elapsed seconds (incrementing). */
  const [clockSeconds, setClockSeconds] = useState(0);
  const clockRef = useRef(0);
  /** Limit at game start — used to compute elapsed time for reports */
  const puzzleTimeLimitRef = useRef(300);
  /** True once all pieces are correct — stops countdown during solved reveal */
  const puzzleSolvedRef = useRef(false);
  const finishedRef = useRef(false);
  /** Pieces currently in the correct slot (updated by puzzle). */
  const correctPlacedCountRef = useRef(0);

  const rows = useMemo(
    () => parseGridDim(themeData?.row, 5),
    [themeData?.row]
  );
  const cols = useMemo(
    () => parseGridDim(themeData?.col, 5),
    [themeData?.col]
  );

  const pieceType = useMemo(
    () => (themeData ? pickJigsawPieceType(themeData) : "classic"),
    [themeData]
  );

  const puzzleType = useMemo(
    () => pickPuzzleTypeFromThemeData(themeData),
    [themeData]
  );

  const countdownTimerEnabled = useMemo(
    () => themeUsesCountdownTimer(themeData),
    [themeData?.use_timeout]
  );

  const puzzleImageUrl = useMemo(
    () => buildThemeAssetUrl(themeData?.puzzle_image),
    [themeData?.puzzle_image]
  );

  const rulesList = useMemo(
    () => parseThemeRules(themeData?.rules),
    [themeData?.rules]
  );

  const [rulesOpen, setRulesOpen] = useState(false);
  const [screenWidth, setScreenWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  /** null = checking fetchReport; true = may play; false = already played, redirecting */
  const [playAllowed, setPlayAllowed] = useState(null);
  /** Demo session (`sessionId` starts with `demo`): visible seconds until plan Swal */
  const [demoPlanCountdownSec, setDemoPlanCountdownSec] = useState(null);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch]);

  useEffect(() => {
    const onResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    dispatch(setBackButtonUrl("/rules"));
  }, [dispatch, status, user]);

  useLayoutEffect(() => {
    if (!sessionIdStartsWithDemo(user)) {
      setDemoPlanCountdownSec(null);
      return;
    }
    setDemoPlanCountdownSec(10);
    let sec = 10;
    const id = window.setInterval(() => {
      sec -= 1;
      if (sec > 0) {
        setDemoPlanCountdownSec(sec);
        return;
      }
      window.clearInterval(id);
      setDemoPlanCountdownSec(null);
      window.setTimeout(() => {
        Swal.fire({
          text: "Subscribe to any PLAN to play with your peers.",
          icon: "info",
          confirmButtonText: "OK",
        }).then((result) => {
          if (result.isConfirmed) {
            redirectToPlansPage(navigate);
          }
        });
      }, 0);
    }, 1000);
    return () => window.clearInterval(id);
  }, [user?.sessionId, navigate]);

  useEffect(() => {
    if (themeStatus !== "succeeded" || !themeData) return;

    if (!user) {
      setPlayAllowed(true);
      return;
    }

    if (isDemoGameBypass(user)) {
      setPlayAllowed(true);
      return;
    }

    const { userId, sessionId, organizationId } = user;
    if (!userId || !sessionId || !organizationId) {
      setPlayAllowed(true);
      return;
    }

    let cancelled = false;

    fetchReport({ userId, sessionId, organizationId })
      .then((data) => {
        if (cancelled) return;
        if (data?.report) {
          setPlayAllowed(false);
          const statusRaw = String(
            data.report.status ?? data.report.ans ?? ""
          ).toLowerCase();
          const puzzleOutcome =
            statusRaw === "completed" ? "completed" : "incomplete";
          const { totalSeconds } = parseThemeTimer(themeData.timer);
          const reportPts = data.report.points ?? data.report.score;
          const ptsNum = Number(reportPts);
          const reportTimeHms = data.report.time1 ?? data.report.time ?? null;
          const givenTimeLabel = themeUsesCountdownTimer(themeData)
            ? formatGivenTimeLabel(totalSeconds)
            : "Unlimited time";
          navigate("/thankyou", {
            state: {
              puzzleOutcome,
              givenTimeLabel,
              points: Number.isFinite(ptsNum) ? ptsNum : 0,
              completionTimeHms: reportTimeHms,
            },
          });
        } else {
          setPlayAllowed(true);
        }
      })
      .catch(() => {
        if (!cancelled) setPlayAllowed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [themeStatus, themeData, user, navigate]);

  const handleTimeoutRef = useRef(null);

  const handlePuzzleSolved = useCallback(() => {
    puzzleSolvedRef.current = true;
  }, []);

  const handleTimeout = useCallback(async () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    const piecesPlaced = correctPlacedCountRef.current;
    clearPuzzleTimerSession(themeData, user, rows, cols);
    const limit = puzzleTimeLimitRef.current;
    const elapsedSec = limit;
    const timeStr = formatClock(limit);
    const reportData = {
      sessionId: user?.sessionId,
      role: user?.role,
      token: user?.token || null,
      name: user?.name,
      userId: user?.userId,
      gameId: user?.gameId || "",
      organizationId: user?.organizationId,
      points: 0,
      time: timeStr,
      reportId: user?.reportId || null,
      ans: "incomplete",
    };
    const thankYouState = {
      puzzleOutcome: "incomplete",
      givenTimeLabel: countdownTimerEnabled
        ? formatGivenTimeLabel(limit)
        : "Unlimited time",
      points: 0,
      elapsedSeconds: elapsedSec,
      completionTimeHms: timeStr,
      piecesPlaced,
    };

    try {
      if (isDemoGameBypass(user)) {
        navigate("/thankyou", { state: thankYouState });
        return;
      }

      const url = `${process.env.REACT_APP_BACKEND_URL}/submitReport`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.sessionId}&${user?.organizationId}`,
        },
        body: JSON.stringify({
          userId: user?.userId,
          email: user?.email,
          name: user?.name,
          role: user?.role,
          sessionId: user?.sessionId,
          organizationId: user?.organizationId,
          points: 0,
          time: timeStr,
          ans: "incomplete",
          currentTheme: themeData?.themename ?? themeData?.themeName ?? null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      console.log("data", data);
      if (!res.ok) {
        const errorMessage = data.error || "Failed to submit";
        Swal.fire("", errorMessage, "error");
        return;
      }
      try {
        await sendReport(reportData);
      } catch (e) {
        Swal.fire("", e.message || "Failed to sync report", "error");
        return;
      }
      navigate("/thankyou", { state: thankYouState });
    } catch (e) {
      Swal.fire("", e.message || "Failed to submit ticket", "error");
    }
  }, [user, navigate, themeData, rows, cols, countdownTimerEnabled]);

  const handleComplete = useCallback(async () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    clearPuzzleTimerSession(themeData, user, rows, cols);
    const limit = puzzleTimeLimitRef.current;
    const tick = Math.max(0, clockRef.current);
    const elapsedSec = countdownTimerEnabled
      ? Math.max(0, limit - tick)
      : tick;
    const timeStr = formatClock(elapsedSec);
    const points = parseInt(themeData?.points, 10);
    const reportData = {
      sessionId: user?.sessionId,
      role: user?.role,
      token: user?.token || null,
      name: user?.name,
      userId: user?.userId,
      gameId: user?.gameId || "",
      organizationId: user?.organizationId,
      points: Number.isNaN(points) ? 0 : points,
      time: timeStr,
      reportId: user?.reportId || null,
      ans: "completed",
    };
    console.log("reportData", reportData);
    try {
      if (!isDemoGameBypass(user)) {
        try {
          const url = `${process.env.REACT_APP_BACKEND_URL}/submitReport`;
          const res = await fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.sessionId}&${user?.organizationId}`,
            },
            body: JSON.stringify({
              userId: user?.userId,
              email: user?.email,
              name: user?.name,
              role: user?.role,
              sessionId: user?.sessionId,
              organizationId: user?.organizationId,
              points: Number.isNaN(points) ? 0 : points,
              time: timeStr,
              ans: "completed",
              currentTheme: themeData?.themename ?? themeData?.themeName ?? null,
            }),
          });
          const data = await res.json().catch(() => ({}));
          console.log("data", data);
          if (!res.ok) {
            const errorMessage = data.error || "Failed to submit";
            Swal.fire("", errorMessage, "error");
            return;
          }
          try {
            await sendReport(reportData);
          } catch (e) {
            Swal.fire("", e.message || "Failed to sync report", "error");
            return;
          }
          navigate("/thankyou", {
            state: {
              puzzleOutcome: "completed",
              givenTimeLabel: countdownTimerEnabled
                ? formatGivenTimeLabel(limit)
                : formatGivenTimeLabel(elapsedSec),
              points: Number.isNaN(points) ? 0 : points,
              elapsedSeconds: elapsedSec,
              completionTimeHms: timeStr,
            },
          });
        } catch (e) {
          Swal.fire("", e.message || "Failed to submit ticket", "error");
        } finally {
          // setSubmitLoading(false);
        }
      } else {
        navigate("/thankyou", {
          state: {
            puzzleOutcome: "completed",
            givenTimeLabel: countdownTimerEnabled
              ? formatGivenTimeLabel(limit)
              : formatGivenTimeLabel(elapsedSec),
            points: Number.isNaN(points) ? 0 : points,
            elapsedSeconds: elapsedSec,
            completionTimeHms: timeStr,
          },
        });
      }
    } catch (e) {
      console.warn(e);
    }
  }, [
    user,
    themeData,
    themeData?.points,
    rows,
    cols,
    navigate,
    themeData?.themename,
    themeData?.themeName,
    countdownTimerEnabled,
  ]);

  handleTimeoutRef.current = handleTimeout;

  useEffect(() => {
    if (themeStatus !== "succeeded" || !themeData || playAllowed !== true) return;

    const cdKey = countdownStorageKey(themeData, user, rows, cols);
    const elKey = elapsedStorageKey(themeData, user, rows, cols);

    if (!countdownTimerEnabled) {
      puzzleTimeLimitRef.current = 0;
      const persistedEl = readElapsedPersisted(elKey);
      const startElapsed =
        persistedEl && typeof persistedEl.seconds === "number"
          ? Math.min(86400, Math.max(0, Math.floor(persistedEl.seconds)))
          : 0;
      clockRef.current = startElapsed;
      setClockSeconds(startElapsed);
      writeElapsedPersisted(elKey, startElapsed);
      finishedRef.current = false;
      puzzleSolvedRef.current = false;

      const id = window.setInterval(() => {
        if (finishedRef.current || puzzleSolvedRef.current) return;
        clockRef.current = Math.min(86400, clockRef.current + 1);
        setClockSeconds(clockRef.current);
        writeElapsedPersisted(elKey, clockRef.current);
      }, 1000);
      return () => window.clearInterval(id);
    }

    const demo = isDemoGameBypass(user);
    const { totalSeconds } = parseThemeTimer(themeData.timer);
    const startSeconds = demo ? 10 : totalSeconds;
    puzzleTimeLimitRef.current = startSeconds;

    let persisted = readCountdownPersisted(cdKey);
    if (!persisted) {
      const legacyKey = legacyCountdownStorageKey(user);
      if (legacyKey) {
        const old = readCountdownPersisted(legacyKey);
        if (old && old.allocated === startSeconds) {
          writeCountdownPersisted(cdKey, old.deadline, old.allocated);
          sessionStorage.removeItem(legacyKey);
          persisted = old;
        }
      }
    }

    let remaining = startSeconds;
    let expiredWhileAway = false;

    if (persisted && persisted.allocated === startSeconds) {
      remaining = Math.floor((persisted.deadline - Date.now()) / 1000);
      if (remaining < 0) {
        remaining = 0;
        expiredWhileAway = true;
      }
    } else {
      writeCountdownPersisted(
        cdKey,
        Date.now() + startSeconds * 1000,
        startSeconds
      );
    }

    clockRef.current = remaining;
    setClockSeconds(remaining);
    finishedRef.current = false;
    puzzleSolvedRef.current = false;

    if (expiredWhileAway && startSeconds > 0) {
      window.setTimeout(() => handleTimeoutRef.current?.(), 0);
    }

    const id = window.setInterval(() => {
      if (finishedRef.current || puzzleSolvedRef.current) return;
      if (clockRef.current <= 0) return;
      clockRef.current = Math.max(0, clockRef.current - 1);
      setClockSeconds(clockRef.current);
      if (clockRef.current <= 0) {
        handleTimeoutRef.current?.();
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [
    themeStatus,
    playAllowed,
    countdownTimerEnabled,
    rows,
    cols,
    themeData?.timer,
    themeData?.themename,
    themeData?.themeName,
    user?.sessionId,
    user?.userId,
    user?.organizationId,
    user?.role,
  ]);

  const buttonColor = themeData?.button_color || "#e9695e";
  const buttonTextColor =
    themeData?.button_text_color || themeData?.button_Textcolor || "#ffffff";
  const titleColor = themeData?.landing_page_title_color || "#000000";

  if (themeStatus === "loading" || !themeData) {
    return (
      <div className="jigsaw-game-page">
        <div className="jigsaw-puzzle-loading" style={{ minHeight: "60vh" }}>
          <div className="quiz-loader" />
        </div>
      </div>
    );
  }

  if (playAllowed !== true) {
    return (
      <div className="jigsaw-game-page">
        <div className="jigsaw-puzzle-loading" style={{ minHeight: "60vh" }}>
          <div className="quiz-loader" />
        </div>
      </div>
    );
  }

  const bgImage = themeBackgroundImageUrl(themeData);

  const isDemoPlanSession = sessionIdStartsWithDemo(user);
  const showDemoOnlyCountdown =
    isDemoPlanSession &&
    demoPlanCountdownSec !== null &&
    demoPlanCountdownSec > 0;
  const showPuzzleTimerChip = !isDemoPlanSession || showDemoOnlyCountdown;

  return (
    <div
      className="jigsaw-game-page"
      style={bgImage ? { backgroundImage: `url(${bgImage})` } : {}}
    >
      {showPuzzleTimerChip ? (
        <div
          className="jigsaw-timer"
          style={{
            backgroundColor: buttonColor,
            color: buttonTextColor,
          }}
        >
          {showDemoOnlyCountdown ? (
            <span
              className="jigsaw-demo-session-countdown"
              role="timer"
              aria-live="polite"
            >
              Demo ends in {demoPlanCountdownSec}s
            </span>
          ) : (
            <>
              {countdownTimerEnabled ? (
                <span className="jigsaw-timer-main">
                  Time left: {formatDisplayMmSs(clockSeconds)}
                </span>
              ) : (
                <span className="jigsaw-timer-main">
                  Timer: {formatDisplayMmSs(clockSeconds)}
                </span>
              )}
            </>
          )}
        </div>
      ) : null}

      <div className="jigsaw-game-inner">
        <h1 className="jigsaw-game-title" style={{ color: themeData.text_color }}>
          {themeData.landing_page_title || "Solve the jigsaw puzzle."}
        </h1>

        <div className="jigsaw-game-main">
          {puzzleImageUrl ? (
            <div className="jigsaw-game-preview-col">
              <div className="jigsaw-preview-wrap">
                <img
                  src={puzzleImageUrl}
                  alt="Reference"
                  className="jigsaw-preview-img"
                />
              </div>
            </div>
          ) : null}
          <div className="jigsaw-puzzle-wrap">
            {puzzleImageUrl ? (
              <>
      
                <JigsawPuzzle
                  key={`${puzzleType}-${rows}-${cols}`}
                  imageUrl={puzzleImageUrl}
                  rows={rows}
                  cols={cols}
                  screenWidth={screenWidth}
                  pieceType={pieceType}
                  puzzleType={puzzleType}
                  onComplete={handleComplete}
                  onPuzzleSolved={handlePuzzleSolved}
                  onCorrectPieceCountChange={(n) => {
                    correctPlacedCountRef.current = n;
                  }}
                />
              </>
            ) : (
              <p style={{ color: titleColor }}>No puzzle image configured for this theme.</p>
            )}
          </div>
        </div>
      </div>

      {rulesOpen && (
        <div
          className="jigsaw-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setRulesOpen(false)}
        >
          <div className="jigsaw-modal" onClick={(e) => e.stopPropagation()}>
            <div className="jigsaw-modal-title">Rules</div>
            <ul className="jigsaw-modal-list">
              {rulesList.length ? (
                rulesList.map((r, i) => (
                  <li key={i}>{r}</li>
                ))
              ) : puzzleType === "square" ? (
                <li>Drag pieces to swap them. Put each piece in its correct place.</li>
              ) : (
                <li>
                  Drag pieces on the board. Release near a slot to snap; wrong pieces swap
                  places. Empty slots show a light patch (no picture hint) until filled.
                </li>
              )}
            </ul>
            <p className="jigsaw-modal-hint">
              {puzzleType === "square"
                ? "On mobile: drag to move; release on another cell to swap."
                : "On touch: drag with your finger; pieces snap to the nearest slot when you release."}
            </p>
            <button
              type="button"
              className="jigsaw-modal-close"
              style={{ backgroundColor: buttonColor, color: buttonTextColor }}
              onClick={() => setRulesOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Game;
